import { getAuthAxiosInstance as authAxios } from './axios';
import { CreateAccount, ImportAccount } from '../types/account';
import {
  CreateTransaction,
  TxInfo,
  UpdateThresholdData,
  UpdateMetadataData,
} from '../types/transactions';
import { REACT_APP_BACKEND_URL } from '../utils/constants';
import { CreateTokenType } from 'types/token';

export const createMultisigAccount = async (accountData: CreateAccount) => {
  return authAxios()?.post(`${REACT_APP_BACKEND_URL}/wallet`, accountData);
};

export const getMultisigAccountData = async (accountId: string) => {
  return authAxios()?.get(`${REACT_APP_BACKEND_URL}/wallet/${accountId}`);
};

export const importMultisigAccount = async (accountData: ImportAccount) => {
  return authAxios()?.post(`${REACT_APP_BACKEND_URL}/wallet/import`, accountData);
};

export const createContractTransaction = async (
  walletId: string,
  transactionData: CreateTransaction,
) => {
  return authAxios()?.post(
    `${REACT_APP_BACKEND_URL}/wallet/${walletId}/interaction`,
    transactionData,
  );
};

export const getMultisigAccounts = async () => {
  return authAxios()?.get(`${REACT_APP_BACKEND_URL}/wallet`);
};

export const createSendTransaction = async (
  tx: TxInfo,
  sendTokenResponse: { txId: string; scheduleId: string },
  walletId: string,
  description?: string,
  waitForExpiry?: boolean,
  expiresAt?: number,
) => {
  const { to, amount } = tx;
  return authAxios()?.post(`${REACT_APP_BACKEND_URL}/wallet/${walletId}/transaction`, {
    amount,
    description: description ?? 'description',
    toAccountId: to,
    transactionId: sendTokenResponse.txId.toString(),
    scheduleId: sendTokenResponse.scheduleId.toString(),
    waitForExpiry: waitForExpiry || false,
    expiresAt,
  });
};

export const getWalletTransactions = async (
  walletId: string,
  page: number = 1,
  limit: number = 10,
  sortBy?: string,
  sortOrder?: 'asc' | 'desc',
  type?: string,
  status?: string,
  direction?: 'incoming' | 'outgoing',
) => {
  let url = `${REACT_APP_BACKEND_URL}/wallet/${walletId}/transactions?page=${page}&limit=${limit}`;
  if (sortBy) url += `&sortBy=${sortBy}`;
  if (sortOrder) url += `&sortOrder=${sortOrder}`;
  if (type) url += `&type=${type}`;
  if (status) url += `&status=${status}`;
  if (direction) url += `&direction=${direction}`;
  return authAxios()?.get(url);
};

export const signCheck = async (
  walletId: string,
  signedTransactionId: string,
  transactionId: string,
  accountId: string,
) => {
  return authAxios()?.post(
    `${REACT_APP_BACKEND_URL}/wallet/${walletId}/transaction/${transactionId}/signature`,
    {
      accountId,
      transactionId: signedTransactionId,
    },
  );
};

/**
 * Create a token association transaction with manual multi-signature support.
 * Stores the signed transaction bytes with the first signer's signature embedded.
 */
export const createTokenAssociationTransaction = async (
  walletId: string,
  data: {
    tokenId: string;
    tokenName?: string;
    tokenSymbol?: string;
    frozenTxBytes: string; // Base64 encoded FROZEN transaction bytes (unsigned base)
    signedTxBytes: string; // Base64 encoded signed transaction bytes (with signer's signature)
    publicKey: string; // DER encoded public key of the signer
    description?: string;
    expiresAt?: number;
    waitForExpiry?: boolean;
  },
) => {
  return authAxios()?.post(`${REACT_APP_BACKEND_URL}/wallet/${walletId}/token-associate`, data);
};

/**
 * Add a manual signature to a token association transaction.
 * Sends updated transaction bytes with the new signature embedded.
 */
export const addManualSignature = async (
  walletId: string,
  transactionId: string,
  data: {
    accountId: string;
    publicKey: string; // DER encoded public key
    signedTxBytes: string; // Base64 encoded updated transaction bytes (with new signature embedded)
  },
) => {
  return authAxios()?.post(
    `${REACT_APP_BACKEND_URL}/wallet/${walletId}/transaction/${transactionId}/manual-signature`,
    data,
  );
};

/**
 * Execute a token association transaction that has met the signature threshold.
 * Called when validStart time has been reached and all required signatures collected.
 */
export const executeTokenAssociation = async (walletId: string, transactionId: string) => {
  return authAxios()?.post(
    `${REACT_APP_BACKEND_URL}/wallet/${walletId}/transaction/${transactionId}/execute`,
  );
};

export const createTokenTransferTransaction = async (
  walletId: string,
  data: {
    toAccountId: string;
    amount: number;
    tokenId: string;
    tokenSymbol: string;
    tokenDecimals: number;
    transactionId: string;
    scheduleId: string;
    description?: string;
    waitForExpiry?: boolean;
    expiresAt?: number;
  },
) => {
  return authAxios()?.post(`${REACT_APP_BACKEND_URL}/wallet/${walletId}/token-transfer`, data);
};

export const createToken = async (walletId: string, tokenData: CreateTokenType) => {
  return authAxios()?.post(`${REACT_APP_BACKEND_URL}/wallet/${walletId}/create-token`, tokenData);
};

export const createThresholdUpdate = async (walletId: string, data: UpdateThresholdData) => {
  return authAxios()?.post(`${REACT_APP_BACKEND_URL}/wallet/${walletId}/threshold-update`, data);
};

export const updateMultisigMetadata = async (walletId: string, data: UpdateMetadataData) => {
  return authAxios()?.patch(`${REACT_APP_BACKEND_URL}/wallet/${walletId}/metadata`, data);
};

export const acknowledgeInvitation = async (walletId: string) => {
  return authAxios()?.post(`${REACT_APP_BACKEND_URL}/wallet/${walletId}/acknowledge`);
};

export const fundWallet = async (
  walletId: string,
  amount: number,
  transactionId: string,
  tokenInfo?: {
    tokenId: string;
    tokenSymbol: string;
    tokenName: string;
    tokenDecimals: number;
  },
) => {
  return authAxios()?.post(`${REACT_APP_BACKEND_URL}/wallet/${walletId}/fund`, {
    amount,
    transactionId,
    ...tokenInfo,
  });
};

export const syncWalletTransactions = async (walletId: string, signal?: AbortSignal) => {
  return authAxios()?.post(
    `${REACT_APP_BACKEND_URL}/wallet/${walletId}/transactions/sync`,
    {},
    { signal },
  );
};
